import { ensureAccessToken } from "./auth";
import { chatApiBase, isChatConfigured } from "./config";

export const TOMBSTONE = "Removed by a moderator";

export type ChatMessage = {
  channel_id: string;
  seq: number;
  user_id: string;
  user: string;
  body: string;
  server_ts_unix_ms: number;
  kind: string;
  target_seq?: number;
};

export type ChatChannel = {
  channel_id: string;
  source: string;
  title: string;
  role: string;
  muted?: boolean;
  pinned?: ChatMessage | null;
};

export type ChatEvent =
  | { event: "message"; message: ChatMessage }
  | { event: "gap"; before_seq: number }
  | { event: "ping" };

export function displayBody(message: ChatMessage): string {
  if ((message.kind === "chat" || message.kind === "announcement") && message.body === "") {
    return TOMBSTONE;
  }
  return message.body;
}

export function applyLive(messages: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  if ((incoming.kind === "redact" || incoming.kind === "restore") && incoming.target_seq) {
    const body = incoming.kind === "redact" ? "" : incoming.body;
    return messages.map((message) =>
      message.seq === incoming.target_seq ? { ...message, body } : message,
    );
  }
  if (messages.some((message) => message.seq === incoming.seq)) return messages;
  return [...messages, incoming];
}

export function parseSSEBlock(block: string): ChatEvent | null {
  const trimmed = block.trim();
  if (!trimmed || trimmed.startsWith(":")) return { event: "ping" };
  let name = "message";
  const data: string[] = [];
  for (const line of trimmed.split("\n")) {
    if (line.startsWith("event:")) name = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }
  if (data.length === 0) return null;
  const payload = JSON.parse(data.join("\n")) as ChatMessage & { before_seq?: number };
  if (name === "gap") return { event: "gap", before_seq: Number(payload.before_seq ?? 0) };
  if (name === "message") return { event: "message", message: payload };
  return null;
}

export function parseSSEBuffer(buffer: string): { events: ChatEvent[]; rest: string } {
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  const events = parts
    .map((part) => parseSSEBlock(part))
    .filter((event): event is ChatEvent => event !== null && event.event !== "ping");
  return { events, rest };
}

async function chatFetch(path: string, init: RequestInit = {}): Promise<Response> {
  if (!isChatConfigured()) throw new Error("Chat is not configured");
  const token = await ensureAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(`${chatApiBase()}${path}`, { ...init, headers });
}

async function chatJSON<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await chatFetch(path, init);
  const text = await resp.text();
  const data = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    const message = (data as { error?: string } | null)?.error ?? resp.statusText;
    throw new Error(message || `Chat error ${resp.status}`);
  }
  return data as T;
}

export function fetchChatChannel(channelId: string): Promise<ChatChannel> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}`);
}

export function fetchChatHistory(channelId: string): Promise<{ latest_seq: number; messages: ChatMessage[] }> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/history`);
}

export function postChatMessage(channelId: string, body: string, kind = "chat"): Promise<ChatMessage> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/messages`, {
    method: "POST",
    body: JSON.stringify({ body, kind }),
  });
}

export function markChatRead(channelId: string, seq: number): Promise<void> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/read`, {
    method: "POST",
    body: JSON.stringify({ seq }),
  });
}

export function setChatMuted(channelId: string, muted: boolean): Promise<void> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/mute`, {
    method: "POST",
    body: JSON.stringify({ muted }),
  });
}

export function redactChatMessage(channelId: string, targetSeq: number): Promise<void> {
  return chatJSON(`/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/redactions`, {
    method: "POST",
    body: JSON.stringify({ target_seq: targetSeq }),
  });
}

export async function fetchRoomUnread(): Promise<boolean> {
  if (!isChatConfigured()) return false;
  const data = await chatJSON<{ channels?: { unread?: boolean; muted?: boolean }[] }>(
    "/api/v1/protected/chat/channels",
  );
  return (data.channels ?? []).some((channel) => channel.unread && !channel.muted);
}

export async function streamChat(
  channelId: string,
  afterSeq: number,
  signal: AbortSignal,
  onEvent: (event: ChatEvent) => void,
): Promise<void> {
  const resp = await chatFetch(
    `/api/v1/protected/chat/channels/${encodeURIComponent(channelId)}/stream?after_seq=${afterSeq}`,
    { headers: { Accept: "text/event-stream" }, signal },
  );
  if (!resp.ok || !resp.body) throw new Error("Chat stream failed");
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseSSEBuffer(buffer);
    buffer = parsed.rest;
    for (const event of parsed.events) onEvent(event);
  }
}
